import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faArrowRight,
  faArrowLeft,
  faCreditCard,
  faShieldHalved,
  faReceipt,
  faLock,
  faBuildingColumns,
  faTag,
  faTicketAlt
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import FawaterakService from '../Services/FawaterakService';
import { storageHelper } from '../../Helpers/storageHelper';
import { toastHelper } from '../../Helpers/toastHelper';
import MiniLoader from '../Shared/MiniLoader';
import Header from '../Shared/Header';
import { encryptData } from '../../utils/encryption';

const FawaterakPaymentPage = ({ target_entity: propTargetEntity, plan_id: propPlanId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId, packageDetails, plan_id: statePlanId } = location.state || {};
  const target_entity = propTargetEntity || location.state?.target_entity;
  const plan_id = propPlanId || statePlanId || planId;

  const [step, setStep] = useState(2);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Coupon states
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);

  useEffect(() => {
    if (!packageDetails) {
      toastHelper.error("تفاصيل الباقة غير متوفرة");
      navigate('/pricing');
      return;
    }
    fetchPaymentMethods();
  }, [packageDetails, navigate]);

  const fetchPaymentMethods = async () => {
    setLoadingMethods(true);
    try {
      const response = await FawaterakService.getPaymentMethods();
      if (response && response.status === 'success' && response.data) {
        setPaymentMethods(response.data);
      } else {
        toastHelper.error("فشل تحميل طرق الدفع");
      }
    } catch (err) {
      toastHelper.error("فشل تحميل طرق الدفع");
    } finally {
      setLoadingMethods(false);
    }
  };

  const handleNextStep = () => {
    if (!selectedMethod) {
      toastHelper.error("الرجاء اختيار طريقة الدفع أولاً");
      return;
    }
    setStep(2);
  };

  const validateCoupon = async () => {
    if (!couponCode || couponCode.trim() === "") {
      setCouponError("الرجاء إدخال كود الخصم");
      return;
    }
    setCouponValidating(true);
    setCouponError("");
    try {
      const response = await ApiServices.get(`Coupon/IsValid?CouponCode=${couponCode.trim()}&TargetEntity=subscription`);
      if (response.check && response.data) {
        setCouponDiscount(response.data);
        toastHelper.success("تم تطبيق كود الخصم بنجاح");
      } else {
        setCouponError(response.msg || "كود الخصم غير صالح");
        setCouponDiscount(0);
      }
    } catch (error) {
      setCouponError("فشل التحقق من كود الخصم");
    } finally {
      setCouponValidating(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponDiscount(0);
    setCouponError("");
  };

  const handleInitiatePayment = async () => {
    setIsProcessing(true);
    try {
      const fullName = storageHelper.getItem("full_name") || "";
      const nameParts = fullName.split(" ");
      const firstName = nameParts[0] || "Student";
      const lastName = nameParts.slice(1).join(" ") || "EMt7any";

      const finalPrice = Math.max(0, (packageDetails?.price || 0) - couponDiscount);

      const payload = {
        cart_total: encryptData(finalPrice),
        currency: "EGP",
        customer_first_name: firstName,
        customer_last_name: lastName,
        customer_email: storageHelper.getItem("user_email") || "info@emthany.com",
        customer_phone: storageHelper.getItem("phone") || "01000000000",
        customer_address: "N/A",
        cart_items: [
          {
            name: packageDetails?.name || "باقة امتحاني",
            price: encryptData(finalPrice),
            quantity: 1
          }
        ],
        merchant_reference_number: `EMT-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        ...(target_entity && { target_entity: encryptData(target_entity) }),
        ...(plan_id && { plan_id: encryptData(plan_id) }),
        ...(couponCode && couponDiscount > 0 && { coupon_used: couponCode })
      };

      const response = await FawaterakService.initiatePayment(payload);
      const checkoutUrl = response?.checkout_url || response?.CheckoutUrl;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toastHelper.error("فشل في بدء عملية الدفع. الرابط غير متوفر.");
        setIsProcessing(false);
      }
    } catch (error) {
      toastHelper.error(error?.message || error?.msg || "حدث خطأ أثناء بدء الدفع");
      setIsProcessing(false);
    }
  };

  if (!packageDetails) return null;

  const themeColor = packageDetails?.color || '#6366f1';

  return (
    <div className="fpp-root" dir="rtl">
      <Header />

      <div className="fpp-bg-shapes">
        <div className="fpp-shape fpp-shape-1" style={{ background: themeColor }}></div>
        <div className="fpp-shape fpp-shape-2"></div>
      </div>

      <div className="fpp-container">

        {/* Floating Stepper OUTSIDE the modal */}
        <div className="fpp-stepper-wrapper">
          <div className="fpp-stepper">
            <div className="fpp-step-track">
              <motion.div
                className="fpp-step-progress"
                style={{ background: themeColor }}
                initial={{ width: '100%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              />
            </div>

            <div className={`fpp-step ${step >= 2 ? 'active' : ''}`}>
              <div className="fpp-step-circle" style={{
                borderColor: step >= 2 ? themeColor : '#e2e8f0',
                background: step >= 2 ? themeColor : '#fff',
                color: step >= 2 ? '#fff' : '#94a3b8',
                boxShadow: step === 2 ? `0 0 20px ${themeColor}66` : 'none'
              }}>
                <FontAwesomeIcon icon={faReceipt} />
              </div>
              <span className="fpp-step-label">تأكيد الطلب</span>
            </div>
          </div>
        </div>

        {/* Modal Card */}
        <motion.div
          className="fpp-modal-card"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", damping: 30, stiffness: 200 }}
        >
          {/* Header of Modal */}
          <div className="fpp-modal-header">
            <div className="fpp-header-info">
              <h2>إتمام الدفع</h2>
              <p>عملية دفع آمنة وسريعة</p>
            </div>
            {!isProcessing && (
              <button className="fpp-back-btn" onClick={() => navigate(-1)}>
                <FontAwesomeIcon icon={faArrowRight} />
                إلغاء وعودة
              </button>
            )}
          </div>

          {/* Dynamic Content Body */}
          <div className="fpp-modal-body">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 30 }}
                  transition={{ duration: 0.3 }}
                  className="fpp-step-container"
                >
                  <h3 className="fpp-section-title">اختر طريقة الدفع المناسبة</h3>

                  {loadingMethods ? (
                    <div className="fpp-loading-state">
                      <MiniLoader size={50} color={themeColor} />
                      <p>جاري جلب طرق الدفع الآمنة...</p>
                    </div>
                  ) : (
                    <div className="fpp-methods-grid">
                      {paymentMethods.map((method) => {
                        const isSelected = selectedMethod?.payment_method_id === method.payment_method_id;
                        return (
                          <motion.div
                            key={method.payment_method_id}
                            className={`fpp-method-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => setSelectedMethod(method)}
                            whileHover={{ y: -4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                            whileTap={{ scale: 0.98 }}
                            style={{
                              borderColor: isSelected ? themeColor : '#e2e8f0',
                              backgroundColor: isSelected ? `${themeColor}08` : '#fff'
                            }}
                          >
                            <div className="fpp-method-icon">
                              {method.logo ? (
                                <img src={method.logo} alt={method.name_en} />
                              ) : (
                                <FontAwesomeIcon icon={faBuildingColumns} />
                              )}
                            </div>
                            <h4 className="fpp-method-name">{method.name_ar}</h4>
                            <div className="fpp-method-radio">
                              <div className="fpp-radio-inner" style={{
                                transform: isSelected ? 'scale(1)' : 'scale(0)',
                                background: themeColor
                              }}></div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  <div className="fpp-action-bar">
                    <button
                      className="fpp-btn-glow"
                      onClick={handleNextStep}
                      disabled={!selectedMethod || loadingMethods}
                      style={{
                        background: `linear-gradient(135deg, ${themeColor}, #4f46e5)`,
                        boxShadow: `0 10px 20px -5px ${themeColor}66`
                      }}
                    >
                      المتابعة لتأكيد الطلب
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 30 }}
                  transition={{ duration: 0.3 }}
                  className="fpp-step-container"
                >
                  <div className="fpp-receipt-card">
                    <div className="fpp-receipt-header">
                      <div className="fpp-receipt-icon" style={{ background: `${themeColor}15`, color: themeColor }}>
                        <FontAwesomeIcon icon={faReceipt} />
                      </div>
                      <h3>ملخص الطلب</h3>
                    </div>

                    <div className="fpp-receipt-details">
                      <div className="fpp-row">
                        <span className="fpp-label">اسم الباقة</span>
                        <span className="fpp-value" style={{ color: themeColor }}>{packageDetails?.name}</span>
                      </div>
                      <div className="fpp-row">
                        <span className="fpp-label">مدة الاشتراك</span>
                        <span className="fpp-value">{packageDetails?.period_name}</span>
                      </div>


                      {/* Coupon Area inside Receipt */}
                      <div className="fpp-coupon-area">
                        <AnimatePresence mode="wait">
                          {!showCouponInput && couponDiscount === 0 ? (
                            <motion.button
                              key="toggle"
                              className="fpp-coupon-toggle"
                              onClick={() => setShowCouponInput(true)}
                              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            >
                              <FontAwesomeIcon icon={faTag} />
                              <span>لديك كود خصم؟</span>
                            </motion.button>
                          ) : couponDiscount > 0 ? (
                            <motion.div
                              key="applied"
                              className="fpp-coupon-applied"
                              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                            >
                              <div className="fpp-applied-info">
                                <FontAwesomeIcon icon={faTicketAlt} />
                                <span>خصم الكوبون: -{couponDiscount} ج.م</span>
                              </div>
                              <button className="fpp-coupon-remove" onClick={removeCoupon}>إزالة</button>
                            </motion.div>
                          ) : (
                            <motion.div
                              key="input"
                              className="fpp-coupon-field"
                              initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                            >
                              <div className="fpp-coupon-input-group">
                                <input
                                  type="text" placeholder="أدخل الكود"
                                  value={couponCode}
                                  onChange={(e) => setCouponCode(e.target.value)}
                                />
                                <button onClick={validateCoupon} disabled={couponValidating} style={{ background: themeColor }}>
                                  {couponValidating ? <MiniLoader size={16} color="#fff" /> : "تطبيق"}
                                </button>
                              </div>
                              {couponError && <div className="fpp-coupon-err">{couponError}</div>}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="fpp-receipt-divider">
                      <div className="fpp-cut-left"></div>
                      <div className="fpp-dash-line"></div>
                      <div className="fpp-cut-right"></div>
                    </div>

                    <div className="fpp-receipt-total">
                      <span className="fpp-total-label">الإجمالي المطلوب</span>
                      <div className="fpp-total-price">
                        <span className="fpp-amount">{Math.max(0, (packageDetails?.price || 0) - couponDiscount)}</span>
                        <span className="fpp-currency">ج.م</span>
                      </div>
                    </div>
                  </div>
                  <div className="fpp-action-bar fpp-dual-actions">

                    <button
                      className="fpp-btn-glow"
                      onClick={handleInitiatePayment}
                      disabled={isProcessing}
                      style={{
                        background: `linear-gradient(135deg, ${themeColor}, #4f46e5)`,
                        boxShadow: `0 10px 20px -5px ${themeColor}80`
                      }}
                    >
                      {isProcessing ? (
                        <>
                          <MiniLoader size={20} color="#fff" />
                          جاري التحويل الآمن...
                        </>
                      ) : (
                        <>
                          تأكيد والدفع الآن
                          <FontAwesomeIcon icon={faShieldHalved} />
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        .fpp-root {
          min-height: 100vh;
          background: #f1f5f9;
          font-family: "Zain", sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* Ambient Background Shapes */
        .fpp-bg-shapes {
          position: absolute;
          inset: 0;
          overflow: hidden;
          z-index: 0;
          pointer-events: none;
        }

        .fpp-shape {
          position: absolute;
          filter: blur(120px);
          opacity: 0.15;
          border-radius: 50%;
        }

        .fpp-shape-1 {
          width: 600px;
          height: 600px;
          top: -100px;
          right: -100px;
        }

        .fpp-shape-2 {
          width: 500px;
          height: 500px;
          bottom: -50px;
          left: -100px;
          background: #3b82f6;
        }

        .fpp-container {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 120px 20px 60px;
          min-height: calc(100vh - 80px);
          max-width: 700px;
          margin: 0 auto;
        }

        /* Floating Stepper */
        .fpp-stepper-wrapper {
          width: 100%;
          margin-bottom: 40px;
          padding: 0 40px;
        }

        .fpp-stepper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
        }

        .fpp-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 2;
        }

        .fpp-step-circle {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 3px solid;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fpp-step-label {
          font-size: 1.1rem;
          font-weight: 800;
          color: #64748b;
          transition: 0.3s;
        }

        .fpp-step.active .fpp-step-label {
          color: #0f172a;
        }

        .fpp-step-track {
          flex: 1;
          height: 4px;
          background: #e2e8f0;
          margin: 0 20px;
          position: relative;
          top: -16px;
          border-radius: 2px;
          overflow: hidden;
        }

        .fpp-step-progress {
          height: 100%;
          border-radius: 2px;
        }

        /* Modal Card */
        .fpp-modal-card {
          width: 100%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 32px;
          box-shadow: 
            0 40px 80px -20px rgba(0, 0, 0, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.5) inset;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.8);
        }

        .fpp-modal-header {
          padding: 32px 40px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f1f5f9;
          background: rgba(255, 255, 255, 0.5);
        }

        .fpp-header-info h2 {
          margin: 0 0 4px 0;
          font-size: 1.8rem;
          font-weight: 900;
          color: #0f172a;
        }

        .fpp-header-info p {
          margin: 0;
          color: #64748b;
          font-size: 1.05rem;
          font-weight: 600;
        }

        .fpp-back-btn {
          background: #f1f5f9;
          border: none;
          padding: 10px 16px;
          border-radius: 12px;
          color: #475569;
          font-weight: 700;
          font-family: inherit;
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .fpp-back-btn:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        .fpp-modal-body {
          padding: 40px;
        }

        .fpp-section-title {
          margin: 0 0 24px 0;
          font-size: 1.3rem;
          font-weight: 800;
          color: #0f172a;
        }

        /* Methods Grid */
        .fpp-methods-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 40px;
        }

        .fpp-method-item {
          border: 2px solid;
          border-radius: 20px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          text-align: center;
        }

        .fpp-method-icon {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: #94a3b8;
          padding: 8px;
        }

        .fpp-method-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .fpp-method-name {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 800;
          color: #1e293b;
        }

        .fpp-method-radio {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid #cbd5e1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fpp-method-item.selected .fpp-method-radio {
          border-color: inherit;
        }

        .fpp-radio-inner {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fpp-loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 0;
          gap: 20px;
          color: #64748b;
          font-weight: 700;
          font-size: 1.1rem;
        }

        /* Action Bars */
        .fpp-action-bar {
          display: flex;
          justify-content: flex-end;
          margin-top: 16px;
        }

        .fpp-dual-actions {
          justify-content: space-between;
          gap: 16px;
        }

        .fpp-btn-glow {
          border: none;
          color: white;
          padding: 18px 32px;
          border-radius: 16px;
          font-size: 1.2rem;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          transition: all 0.3s;
          width: 100%;
        }

        .fpp-dual-actions .fpp-btn-glow {
          flex: 2;
        }

        .fpp-btn-glow:hover:not(:disabled) {
          transform: translateY(-2px);
          filter: brightness(1.1);
        }

        .fpp-btn-glow:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          filter: grayscale(0.5);
        }

        .fpp-btn-outline {
          flex: 1;
          background: #fff;
          border: 2px solid #e2e8f0;
          color: #64748b;
          padding: 18px;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .fpp-btn-outline:hover:not(:disabled) {
          border-color: #cbd5e1;
          color: #0f172a;
          background: #f8fafc;
        }

        /* Receipt Card */
        .fpp-receipt-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          position: relative;
          box-shadow: 0 10px 30px -10px rgba(0,0,0,0.05);
          margin-bottom: 24px;
        }

        .fpp-receipt-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 24px 32px;
          border-bottom: 1px solid #f1f5f9;
        }

        .fpp-receipt-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
        }

        .fpp-receipt-header h3 {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 800;
          color: #0f172a;
        }

        .fpp-receipt-details {
          padding: 32px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .fpp-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .fpp-label {
          color: #64748b;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .fpp-value {
          color: #0f172a;
          font-weight: 800;
          font-size: 1.2rem;
        }

        .fpp-flex-val {
          display: flex;
          align-items: center;
          gap: 8px;
        }

          .fpp-tiny-logo {
          width: 24px;
          height: 24px;
          object-fit: contain;
          background: #f8fafc;
          border-radius: 6px;
          padding: 2px;
          border: 1px solid #e2e8f0;
        }

        /* Coupon Styles */
        .fpp-coupon-area {
          margin-top: 10px;
          padding-top: 20px;
          border-top: 1px dashed #e2e8f0;
        }

        .fpp-coupon-toggle {
          width: 100%;
          background: transparent;
          border: 1px dashed #cbd5e1;
          color: #64748b;
          padding: 12px;
          border-radius: 12px;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .fpp-coupon-toggle:hover {
          border-color: #6366f1;
          color: #6366f1;
          background: #f5f3ff;
        }

        .fpp-coupon-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .fpp-coupon-input-group {
          display: flex;
          gap: 8px;
        }

        .fpp-coupon-field input {
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          font-family: inherit;
          font-size: 1rem;
          outline: none;
          transition: 0.2s;
        }

        .fpp-coupon-field input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .fpp-coupon-field button {
          border: none;
          color: #fff;
          padding: 0 20px;
          border-radius: 12px;
          font-family: inherit;
          font-weight: 700;
          cursor: pointer;
          transition: 0.2s;
        }

        .fpp-coupon-field button:hover:not(:disabled) {
          filter: brightness(1.1);
        }

        .fpp-coupon-field button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .fpp-coupon-err {
          color: #ef4444;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .fpp-coupon-applied {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          padding: 12px 16px;
          border-radius: 12px;
        }

        .fpp-applied-info {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #10b981;
          font-weight: 700;
        }

        .fpp-coupon-remove {
          background: transparent;
          border: none;
          color: #ef4444;
          font-family: inherit;
          font-weight: 700;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 4px 8px;
        }
        .fpp-coupon-remove:hover {
          text-decoration: underline;
        }

        .fpp-receipt-divider {
          position: relative;
          height: 24px;
          display: flex;
          align-items: center;
        }

        .fpp-cut-left, .fpp-cut-right {
          position: absolute;
          width: 24px;
          height: 24px;
          background: #f1f5f9;
          border-radius: 50%;
          border: 1px solid #e2e8f0;
        }

        .fpp-cut-left {
          left: -12px;
          border-left-color: transparent;
          border-top-color: transparent;
          border-bottom-color: transparent;
        }

        .fpp-cut-right {
          right: -12px;
          border-right-color: transparent;
          border-top-color: transparent;
          border-bottom-color: transparent;
        }

        .fpp-dash-line {
          flex: 1;
          margin: 0 16px;
          border-top: 2px dashed #cbd5e1;
        }

        .fpp-receipt-total {
          padding: 24px 32px 32px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }

        .fpp-total-label {
          font-size: 1.3rem;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 8px;
        }

        .fpp-total-price {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }

        .fpp-amount {
          font-size: 2.8rem;
          font-weight: 900;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -1px;
        }

        .fpp-currency {
          font-size: 1.2rem;
          font-weight: 800;
          color: #64748b;
        }

        .fpp-security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #10b981;
          font-size: 1.05rem;
          font-weight: 700;
          background: #ecfdf5;
          padding: 16px;
          border-radius: 16px;
          margin-bottom: 32px;
        }

        @media (max-width: 640px) {
          .fpp-container {
            padding: 90px 12px 30px;
          }

          .fpp-stepper-wrapper {
            padding: 0 5px;
            margin-bottom: 24px;
          }

          .fpp-step-circle {
            width: 44px;
            height: 44px;
            font-size: 1.1rem;
          }

          .fpp-modal-card {
            border-radius: 20px;
          }

          .fpp-modal-header {
            padding: 20px 16px;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .fpp-header-info h2 {
            font-size: 1.5rem;
          }

          .fpp-back-btn {
            width: 100%;
            justify-content: center;
          }

          .fpp-modal-body {
            padding: 20px 16px;
          }

          .fpp-methods-grid {
            grid-template-columns: 1fr;
            gap: 12px;
            margin-bottom: 24px;
          }

          .fpp-method-item {
            padding: 16px;
            flex-direction: row;
            justify-content: flex-start;
            gap: 16px;
          }

          .fpp-method-icon {
            width: 48px;
            height: 48px;
            font-size: 1.5rem;
            flex-shrink: 0;
          }

          .fpp-method-name {
            font-size: 1.1rem;
            text-align: right;
            flex: 1;
            margin: 0;
          }

          .fpp-method-radio {
            top: 50%;
            right: auto;
            left: 16px;
            transform: translateY(-50%);
          }

          .fpp-dual-actions {
            flex-direction: column;
          }

          .fpp-btn-glow {
            width: 100%;
            padding: 16px;
            font-size: 1.1rem;
          }

          .fpp-receipt-card {
            border-radius: 20px;
          }

          .fpp-receipt-header {
            padding: 16px;
          }

          .fpp-receipt-details {
            padding: 16px;
            gap: 16px;
          }

          .fpp-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          .fpp-value {
            font-size: 1.1rem;
          }

          .fpp-coupon-input-group {
            flex-direction: column;
          }

          .fpp-coupon-field button {
            width: 100%;
            padding: 12px;
          }

          .fpp-receipt-total {
            padding: 16px;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .fpp-amount {
            font-size: 2.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default FawaterakPaymentPage;
